import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ToastContainer from '../../components/ui/Toast';

import SubscriptionCard from './components/SubscriptionCard';
import SubscriptionDetailModal from './components/SubscriptionDetailModal';
import CreateSubscriptionModal from './components/CreateSubscriptionModal';
import FilterPanel from './components/FilterPanel';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

/**
 * Convert a stripe_subscriptions table row (snake_case) into the camelCase
 * shape expected by the rest of the dashboard.
 */
function rowToSubscription(row) {
  return {
    id: row.id,
    customerName: row.customer_name,
    customerEmail: row.customer_email,
    plan: row.plan,
    amount: Number(row.amount),
    currency: row.currency,
    interval: row.interval,
    status: row.status,
    currentPeriodStart: row.current_period_start,
    currentPeriodEnd: row.current_period_end,
    nextBillingDate: row.next_billing_date,
    cancelAtPeriodEnd: row.cancel_at_period_end,
    trialEnd: row.trial_end,
    createdAt: row.stripe_created_at,
    metadata: row.metadata ?? {},
  };
}

const SubscriptionManagementDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterInterval, setFilterInterval] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Toast notifications
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  // Deduplication: tracks "subscriptionId:eventType" -> timestamp of last toast
  const recentToastKeysRef = useRef({});

  const addToast = useCallback((message, type = 'info') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  /**
   * Returns true if a toast for this key was shown within the dedup window (ms).
   * If not, records the key and returns false.
   */
  const isDuplicateEvent = useCallback((key, windowMs = 2000) => {
    const now = Date.now();
    const last = recentToastKeysRef.current[key];
    if (last && now - last < windowMs) {
      return true;
    }
    recentToastKeysRef.current[key] = now;
    return false;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Realtime connection status: 'connecting' | 'connected' | 'disconnected' | 'error'
  const [realtimeStatus, setRealtimeStatus] = useState('connecting');

  // Track which subscription IDs are currently highlighted
  const [highlightedIds, setHighlightedIds] = useState(new Set());
  const highlightTimers = useRef({});

  const flashHighlight = useCallback((id) => {
    // Clear any existing timer for this id
    if (highlightTimers.current[id]) {
      clearTimeout(highlightTimers.current[id]);
    }
    setHighlightedIds((prev) => new Set([...prev, id]));
    highlightTimers.current[id] = setTimeout(() => {
      setHighlightedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      delete highlightTimers.current[id];
    }, 2000);
  }, []);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/authentication');
      return;
    }
  }, [user, authLoading, navigate]);

  const fetchSubscriptions = useCallback(async () => {
    setLoadingSubscriptions(true);
    setFetchError(null);
    try {
      const { data, error } = await supabase.functions.invoke('list-subscriptions');
      if (error) throw error;
      setSubscriptions(data?.subscriptions ?? []);
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
      setFetchError(err?.message || 'Failed to load subscriptions. Please try again.');
    } finally {
      setLoadingSubscriptions(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      fetchSubscriptions();
    }
  }, [authLoading, user, fetchSubscriptions]);

  // Subscribe to Supabase Realtime on the stripe_subscriptions table.
  // The stripe-webhook Edge Function writes to this table whenever Stripe fires
  // customer.subscription.updated, customer.subscription.deleted, or
  // invoice.payment_failed, so we see live status changes without a full reload.
  useEffect(() => {
    if (!user) return;

    setRealtimeStatus('connecting');

    const channel = supabase
      .channel('stripe-subscriptions-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'stripe_subscriptions' },
        (payload) => {
          const { eventType, new: newRow, old: oldRow } = payload;

          if (eventType === 'INSERT' || eventType === 'UPDATE') {
            const updated = rowToSubscription(newRow);
            setSubscriptions((prev) => {
              const exists = prev.some((s) => s.id === updated.id);
              if (exists) {
                return prev.map((s) => (s.id === updated.id ? updated : s));
              }
              // New subscription arrived via webhook — prepend it.
              return [updated, ...prev];
            });
            // Keep the detail modal in sync if it's open for this subscription.
            setSelectedSubscription((prev) =>
              prev && prev.id === updated.id ? updated : prev
            );

            // Notify the creator (deduplicated within 2 s per subscription + event type)
            const email = updated.customerEmail || updated.customerName || 'Unknown';
            const status = updated.status?.replace('_', ' ') ?? 'updated';
            const toastKey = `${updated.id}:${eventType}`;
            if (!isDuplicateEvent(toastKey)) {
              if (eventType === 'INSERT') {
                addToast(`New subscription added for ${email}`, 'success');
              } else {
                addToast(`Subscription for ${email} updated to ${status}`, 'info');
              }
            }
            flashHighlight(updated.id);
          } else if (eventType === 'DELETE') {
            // Realtime DELETE payloads carry the old row.
            const deletedId = oldRow?.id;
            if (deletedId) {
              const deletedEmail = oldRow?.customer_email || oldRow?.customer_name || 'Unknown';
              setSubscriptions((prev) => prev.filter((s) => s.id !== deletedId));
              setSelectedSubscription((prev) =>
                prev && prev.id === deletedId ? null : prev
              );
              setShowDetailModal((prev) =>
                selectedSubscription?.id === deletedId ? false : prev
              );
              const deleteKey = `${deletedId}:DELETE`;
              if (!isDuplicateEvent(deleteKey)) {
                addToast(`Subscription for ${deletedEmail} was removed`, 'warning');
              }
            }
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          setRealtimeStatus('connected');
        } else if (status === 'CHANNEL_ERROR' || err) {
          setRealtimeStatus('error');
        } else if (status === 'TIMED_OUT' || status === 'CLOSED') {
          setRealtimeStatus('disconnected');
        }
      });

    return () => {
      setRealtimeStatus('disconnected');
      supabase.removeChannel(channel);
    };
  }, [user, addToast, flashHighlight, isDuplicateEvent]);

  const handleViewDetails = (subscription) => {
    setSelectedSubscription(subscription);
    setShowDetailModal(true);
  };

  const handleCancelSubscription = async (subscriptionId) => {
    try {
      const { error } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'cancel', subscriptionId },
      });
      if (error) throw error;
      setSubscriptions(prev =>
        prev.map(sub =>
          sub.id === subscriptionId
            ? { ...sub, cancelAtPeriodEnd: true }
            : sub
        )
      );
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      alert('Failed to cancel subscription: ' + (err?.message || 'Unknown error'));
    }
  };

  const handleReactivateSubscription = async (subscriptionId) => {
    try {
      const { error } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'reactivate', subscriptionId },
      });
      if (error) throw error;
      setSubscriptions(prev =>
        prev.map(sub =>
          sub.id === subscriptionId
            ? { ...sub, cancelAtPeriodEnd: false, status: 'active' }
            : sub
        )
      );
    } catch (err) {
      console.error('Failed to reactivate subscription:', err);
      alert('Failed to reactivate subscription: ' + (err?.message || 'Unknown error'));
    }
  };

  const filteredSubscriptions = subscriptions?.filter(sub => {
    const matchesSearch =
      sub?.customerName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      sub?.customerEmail?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      sub?.plan?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = filterStatus === 'all' || sub?.status === filterStatus;
    const matchesInterval = filterInterval === 'all' || sub?.interval === filterInterval;
    return matchesSearch && matchesStatus && matchesInterval;
  });

  const sortedSubscriptions = [...filteredSubscriptions]?.sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b?.createdAt) - new Date(a?.createdAt);
      case 'oldest':
        return new Date(a?.createdAt) - new Date(b?.createdAt);
      case 'amount-high':
        return b?.amount - a?.amount;
      case 'amount-low':
        return a?.amount - b?.amount;
      default:
        return 0;
    }
  });

  const stats = {
    total: subscriptions?.length,
    active: subscriptions?.filter(s => s?.status === 'active')?.length,
    trialing: subscriptions?.filter(s => s?.status === 'trialing')?.length,
    pastDue: subscriptions?.filter(s => s?.status === 'past_due')?.length,
    canceled: subscriptions?.filter(s => s?.status === 'canceled')?.length,
    mrr: subscriptions
      ?.filter(s => s?.status === 'active' && s?.interval === 'monthly')
      ?.reduce((sum, s) => sum + s?.amount, 0),
    arr: subscriptions
      ?.filter(s => s?.status === 'active')
      ?.reduce((sum, s) => {
        const monthlyAmount = s?.interval === 'yearly' ? s?.amount / 12 : s?.amount;
        return sum + monthlyAmount;
      }, 0) * 12
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <NavigationBar stats={stats}>
        <div className="flex-1 p-6 lg:p-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Subscription Management</h1>
                <p className="text-muted-foreground mt-1">
                  Monitor and manage recurring payments
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Realtime connection badge */}
                {realtimeStatus === 'connected' && (
                  <span className="flex items-center gap-1.5 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                    </span>
                    Live
                  </span>
                )}
                {(realtimeStatus === 'disconnected' || realtimeStatus === 'error') && (
                  <span className="flex items-center gap-1.5 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
                    <span className="relative flex h-2 w-2">
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                    </span>
                    Reconnecting…
                  </span>
                )}
                {realtimeStatus === 'connecting' && (
                  <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted border border-border rounded-full px-2.5 py-1">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-muted-foreground opacity-50" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-muted-foreground opacity-50" />
                    </span>
                    Connecting…
                  </span>
                )}
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  onClick={fetchSubscriptions}
                  disabled={loadingSubscriptions}
                  iconClassName={loadingSubscriptions ? 'animate-spin' : ''}
                >
                  Refresh
                </Button>
                <Button
                  iconName="Plus"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create Subscription
                </Button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Total Subscriptions</span>
                <Icon name="Users" size={20} className="text-primary" />
              </div>
              <p className="text-3xl font-bold text-foreground">{stats?.total}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {stats?.active} active
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Monthly Recurring Revenue</span>
                <Icon name="TrendingUp" size={20} className="text-green-600" />
              </div>
              <p className="text-3xl font-bold text-foreground">
                ${stats?.mrr?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                From monthly active subscriptions
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Annual Recurring Revenue</span>
                <Icon name="DollarSign" size={20} className="text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-foreground">
                ${stats?.arr?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Projected annual revenue
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Past Due</span>
                <Icon name="AlertCircle" size={20} className="text-amber-600" />
              </div>
              <p className="text-3xl font-bold text-foreground">{stats?.pastDue}</p>
              <p className="text-xs text-amber-600 mt-1">
                {stats?.canceled} canceled
              </p>
            </div>
          </div>

          {/* Filters */}
          <FilterPanel
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filterStatus={filterStatus}
            onStatusChange={setFilterStatus}
            filterInterval={filterInterval}
            onIntervalChange={setFilterInterval}
            sortBy={sortBy}
            onSortChange={setSortBy}
            stats={stats}
          />

          {/* Subscriptions List */}
          <div className="space-y-4">
            {loadingSubscriptions ? (
              <div className="text-center py-12 bg-card border border-border rounded-lg">
                <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading subscriptions...</p>
              </div>
            ) : fetchError ? (
              <div className="text-center py-12 bg-card border border-border rounded-lg">
                <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
                <p className="text-destructive font-medium mb-2">Failed to load subscriptions</p>
                <p className="text-muted-foreground text-sm mb-4">{fetchError}</p>
                <Button variant="outline" iconName="RefreshCw" onClick={fetchSubscriptions}>
                  Try Again
                </Button>
              </div>
            ) : sortedSubscriptions?.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-lg">
                <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {subscriptions.length === 0
                    ? 'No subscriptions found in your Stripe account'
                    : 'No subscriptions found matching your criteria'}
                </p>
              </div>
            ) : (
              sortedSubscriptions?.map(subscription => (
                <SubscriptionCard
                  key={subscription?.id}
                  subscription={subscription}
                  onViewDetails={handleViewDetails}
                  onCancel={handleCancelSubscription}
                  onReactivate={handleReactivateSubscription}
                  highlight={highlightedIds.has(subscription?.id)}
                />
              ))
            )}
          </div>
        </div>
      </NavigationBar>

      {/* Detail Modal */}
      {showDetailModal && selectedSubscription && (
        <SubscriptionDetailModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          subscription={selectedSubscription}
          onCancel={handleCancelSubscription}
          onReactivate={handleReactivateSubscription}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateSubscriptionModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreate={(newSub) => {
            setSubscriptions(prev => [newSub, ...prev]);
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default SubscriptionManagementDashboard;
